import React, { forwardRef, useRef, useState } from 'react';
import { Country, getCountryCallingCode } from 'react-phone-number-input';
import { Combobox } from '@headlessui/react';
import cx from 'classnames';

import CountryFlag from 'components/CountryFlag';

import { GlobeIcon } from './icons';

const CountryButton = forwardRef<HTMLButtonElement, React.ComponentProps<'button'>>(({ children, ...props }, ref) => (
  <button {...props} ref={ref} type="button" tabIndex={0}>
    {children}
  </button>
));
CountryButton.displayName = 'CountryButton';

type CountryOption = { value?: Country; label: string };

type Props = {
  value?: Country;
  onChange: (country?: Country) => void;
  options: CountryOption[];
  disabled?: boolean;
  readOnly?: boolean;
};

function PhoneCountrySelect({ value, onChange, options, disabled, readOnly }: Props) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [query, setQuery] = useState('');
  const search = query
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();
  const matches = options.filter((option) => {
    if (/^\+?\d+$/.test(search)) {
      return option.value && getCountryCallingCode(option.value).startsWith(search.replace(/^\+/, ''));
    }
    const text = `${option.label} ${option.value || ''}`;
    return text
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase()
      .includes(search);
  });
  const selectedLabel = options.find((option) => option.value === value)?.label;

  return (
    <Combobox
      as="div"
      className="flex shrink-0"
      disabled={disabled || readOnly}
      value={value || ''}
      onChange={(country: Country | '') => onChange(country || undefined)}
    >
      {({ open }) => (
        <>
          <Combobox.Button
            as={CountryButton}
            ref={buttonRef}
            onClick={() => setQuery('')}
            aria-label={`${gettext('Change phone prefix')}: ${selectedLabel}`}
            title={selectedLabel}
            className="flex items-center gap-2 rounded-l-xl border-r border-slate-300 px-3 text-retino-darkviolet focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-retino-darkviolet"
          >
            {value ? <CountryFlag key={value} country={value} className="h-3.5 w-5 rounded-sm" /> : <GlobeIcon />}
            <svg className="size-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Combobox.Button>
          {open && (
            <div className="absolute inset-x-0 top-full z-20 mt-2 rounded-xl border border-slate-200 bg-white p-2 shadow-lg">
              <Combobox.Input
                aria-label={gettext('Search country or calling code')}
                placeholder={gettext('Country or calling code')}
                displayValue={() => query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') buttonRef.current?.focus();
                }}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-base text-black placeholder:text-retino-grey focus:border-retino-darkviolet focus:ring-retino-darkviolet sm:text-sm"
              />
              <Combobox.Options className="mt-2 max-h-56 overflow-y-auto overscroll-contain outline-none">
                {matches.map((option) => (
                  <Combobox.Option
                    key={option.value || 'international'}
                    value={option.value || ''}
                    className={({ active }) =>
                      cx(
                        'flex min-h-11 cursor-pointer items-center gap-2 rounded-lg px-2 py-2.5 text-sm text-retino-darkviolet sm:min-h-0',
                        active && 'bg-retino-beige',
                      )
                    }
                  >
                    {option.value ? (
                      <CountryFlag country={option.value} className="h-3.5 w-5 rounded-sm" />
                    ) : (
                      <GlobeIcon />
                    )}
                    <span className="min-w-0 flex-1">{option.label}</span>
                    {option.value && (
                      <span className="shrink-0 text-retino-grey">+{getCountryCallingCode(option.value)}</span>
                    )}
                  </Combobox.Option>
                ))}
                {!matches.length && (
                  <li className="px-2 py-3 text-sm text-retino-grey">{gettext('No countries found.')}</li>
                )}
              </Combobox.Options>
            </div>
          )}
        </>
      )}
    </Combobox>
  );
}

export default PhoneCountrySelect;
